import { Pressable, StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";

// THE shared −/value/+ stepper. Lifted out of NewProgramSetup's local copy when
// the program editor needed the same control to edit cycleWeeks in place (PE-11)
// — one definition so the create-time and edit-time steppers can't drift on
// sizing, disabled treatment, or the ≥44 touch target. a11y: each button spells
// its action ("Weeks +") and disables at the bound; the value is tabular-nums.

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

export type StepperProps = {
  theme: UiTheme;
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
};

export function Stepper({ theme, label, value, min, max, onChange }: StepperProps) {
  const styles = makeStyles(theme);
  return (
    <View style={styles.line}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.controls}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${label} −`}
          disabled={value <= min}
          onPress={() => onChange(clamp(value - 1, min, max))}
          style={[styles.btn, value <= min && styles.btnDisabled]}
        >
          <Text style={styles.btnText}>−</Text>
        </Pressable>
        <Text style={styles.value}>{value}</Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${label} +`}
          disabled={value >= max}
          onPress={() => onChange(clamp(value + 1, min, max))}
          style={[styles.btn, value >= max && styles.btnDisabled]}
        >
          <Text style={styles.btnText}>＋</Text>
        </Pressable>
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    line: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: 48,
    },
    label: { color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    controls: { flexDirection: "row", alignItems: "center", gap: s.s4 },
    btn: {
      width: 48,
      height: 44,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    btnDisabled: { opacity: 0.35 },
    btnText: { color: p.ink, fontSize: t.sizes.lg, fontWeight: "700" },
    value: {
      minWidth: 36,
      textAlign: "center",
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: "800",
      fontVariant: ["tabular-nums"],
    },
  });
}
